import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './ChatRoom.css';

// 导入真实 API 和模拟 API
import * as realApi from '../../api/noMatchApi';
import * as mockApi from '../../api/mockNoMatchApi';

// 控制是否使用模拟数据
const USE_MOCK_API = false; // 设置为 true 使用模拟数据，设置为 false 使用真实 API

// 选择要使用的 API
const api = USE_MOCK_API ? mockApi : realApi;

const ChatRoom = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  
  // 状态管理
  const [chatRoom, setChatRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [typingTimer, setTypingTimer] = useState(null);
  
  // 引用
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const cleanupFunctions = useRef([]);

  // 组件加载时初始化
  useEffect(() => {
    initializeChatRoom();
    
    // 清理函数
    return () => {
      cleanupConnections();
      // 清理输入状态计时器
      if (typingTimer) {
        clearTimeout(typingTimer);
      }
    };
  }, [roomId]);

  // 自动滚动到底部
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // 键盘快捷键支持
  useEffect(() => {
    const handleKeyDown = (e) => {
      // ESC 键返回上一页
      if (e.key === 'Escape') {
        goBack();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // 清理连接
  const cleanupConnections = async () => {
    try {
      // 执行所有清理函数
      cleanupFunctions.current.forEach(cleanup => {
        if (typeof cleanup === 'function') {
          cleanup();
        }
      });
      cleanupFunctions.current = [];
      
      // 如果使用真实 API，断开 SignalR 连接
      if (!USE_MOCK_API && api.leaveChatRoomSignalR && api.disconnectSignalR) {
        await api.leaveChatRoomSignalR(roomId);
        await api.disconnectSignalR();
      }
    } catch (error) {
      console.error('Error cleaning up connections:', error);
    }
  };

  // 初始化聊天室
  const initializeChatRoom = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // 获取用户信息
      const userProfile = await api.getUserProfile();
      if (!userProfile) {
        navigate('/nomatch');
        return;
      }
      
      console.log('User profile loaded:', userProfile);
      setUser(userProfile);
      
      // 获取聊天室信息
      const roomResponse = await api.getChatRoom(roomId);
      if (roomResponse.success) {
        setChatRoom(roomResponse.chatRoom);
      }
      
      // 获取消息历史
      const messagesResponse = await api.getChatMessages(roomId);
      if (messagesResponse.success) {
        setMessages(messagesResponse.messages);
      }
      
      // 建立 SignalR 连接
      if (!USE_MOCK_API) {
        await initializeSignalRConnection(userProfile);
      }
      
    } catch (error) {
      console.error('Error initializing chat room:', error);
      setError('無法載入聊天室，請稍後再試');
    } finally {
      setIsLoading(false);
    }
  };

  // 初始化 SignalR 连接
  const initializeSignalRConnection = async (userProfile) => {
    try {
      // 初始化 SignalR 连接
      const connected = await api.initializeSignalR(userProfile.id, userProfile.authToken);
      
      if (!connected) {
        throw new Error('SignalR 连接失败');
      }

      // 设置消息监听器 - 接收所有消息（包括自己的）
      const messageUnsubscribe = api.onMessage((message) => {
        const isOwnMessage = message.senderId === userProfile.id || 
                           message.senderId === userProfile.id?.toString();
        
        console.log('📨 Received message from SignalR:', {
          senderId: message.senderId,
          currentUserId: userProfile.id,
          content: message.content,
          isOwnMessage,
          willShowOn: isOwnMessage ? 'RIGHT side' : 'LEFT side'
        });
        
        setMessages(prev => {
          // 检查是否已经存在相同的消息，防止重复
          const isDuplicate = prev.some(existingMessage => 
            existingMessage.id === message.id || 
            (existingMessage.content === message.content && 
             existingMessage.senderId === message.senderId &&
             Math.abs(new Date(existingMessage.timestamp) - new Date(message.timestamp)) < 1000)
          );
          
          if (!isDuplicate) {
            console.log('✅ Adding message to chat list');
            return [...prev, message];
          } else {
            console.log('🚫 Prevented duplicate message');
            return prev;
          }
        });
      });
      cleanupFunctions.current.push(messageUnsubscribe);

      // 设置连接状态监听器
      const connectionUnsubscribe = api.onConnection((status, data) => {
        setIsConnected(status === 'connected' || status === 'reconnected');
        if (status === 'disconnected') {
          setError('連接中斷，請重新載入頁面');
        }
      });
      cleanupFunctions.current.push(connectionUnsubscribe);

      // 设置错误监听器
      const errorUnsubscribe = api.onError((message, error) => {
        console.error('SignalR error:', message, error);
        setError(message || '連接發生錯誤');
      });
      cleanupFunctions.current.push(errorUnsubscribe);

      // 设置用户加入监听器
      const userJoinedUnsubscribe = api.onUserJoined((user) => {
        console.log('User joined:', user);
        // 可以在这里添加用户加入的提示
      });
      cleanupFunctions.current.push(userJoinedUnsubscribe);

      // 设置用户离开监听器
      const userLeftUnsubscribe = api.onUserLeft((user) => {
        console.log('User left:', user);
        // 可以在这里添加用户离开的提示
      });
      cleanupFunctions.current.push(userLeftUnsubscribe);

      // 设置打字状态监听器
      const typingUnsubscribe = api.onTyping((userId, isTyping) => {
        // 只处理其他用户的打字状态
        if (userId !== userProfile.id) {
          setIsTyping(isTyping);
        }
      });
      cleanupFunctions.current.push(typingUnsubscribe);

      // 加入聊天室
      const joinResult = await api.joinChatRoom(roomId);
      if (!joinResult) {
        throw new Error('加入聊天室失败');
      }

      setIsConnected(true);
      console.log('SignalR 连接和聊天室加入成功');
      
    } catch (error) {
      console.error('SignalR 连接初始化失败:', error);
      // setError('無法建立即時連接，請重新載入頁面');
    }
  };

  // 滚动到底部
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // 发送消息
  const sendMessage = async () => {
    if (!newMessage.trim() || isSending) return;
    
    const messageContent = newMessage.trim();
    setNewMessage('');
    setIsSending(true);
    
    try {
      if (USE_MOCK_API) {
        // 使用模拟 API
        const response = await api.sendMessage(roomId, messageContent);
        if (response.success) {
          setMessages(prev => [...prev, response.message]);
        }
      } else {
        // 使用 SignalR 发送消息，消息会通过 SignalR 的 onMessage 监听器自动添加
        await api.sendMessageSignalR(roomId, messageContent, 'text');
        console.log('🚀 Message sent via SignalR:', messageContent);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setError('發送消息失敗，請重試');
      setNewMessage(messageContent); // 恢复消息内容
    } finally {
      setIsSending(false);
    }
  };

  // 处理键盘事件 - 参考 AiConsultation 的中文输入处理
  const handleKeyDown = (e) => {
    // 检查是否正在进行中文输入（isComposing属性为true表示正在输入中文）
    if (e.key === 'Enter' && !e.shiftKey && !e.isComposing && !e.nativeEvent.isComposing) {
      e.preventDefault();
      sendMessage();
    }
  };

  // 处理输入变化
  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    
    // 发送打字状态
    if (!USE_MOCK_API && api.sendTypingStatus) {
      const isTypingNow = e.target.value.length > 0;
      api.sendTypingStatus(roomId, isTypingNow);
    }
    
    // 处理输入状态指示 - 在模拟模式下显示对方正在输入
    if (USE_MOCK_API && e.target.value.length > 0) {
      setIsTyping(true);
      
      // 清除之前的计时器
      if (typingTimer) {
        clearTimeout(typingTimer);
      }
      
      // 设置新的计时器，2秒后取消输入状态
      const timer = setTimeout(() => {
        setIsTyping(false);
      }, 2000);
      
      setTypingTimer(timer);
    } else if (e.target.value.length === 0) {
      setIsTyping(false);
      if (typingTimer) {
        clearTimeout(typingTimer);
        setTypingTimer(null);
      }
    }
  };

  // 离开聊天室
  const leaveChatRoom = async () => {
    if (window.confirm('確定要離開這個聊天室嗎？離開後將無法恢復對話。')) {
      try {
        if (USE_MOCK_API) {
          await api.leaveChatRoom(roomId);
        } else {
          await api.leaveChatRoomSignalR(roomId);
        }
        navigate('/nomatch');
      } catch (error) {
        console.error('Error leaving chat room:', error);
        alert('離開聊天室時發生錯誤');
      }
    }
  };

  // 返回上一页
  const goBack = () => {
    // 检查是否有历史记录可以回退
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      // 如果没有历史记录，则跳转到主页
      navigate('/nomatch');
    }
  };

  // 格式化时间 - 参考 AiConsultation 的时间显示
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    
    const time = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    
    // 如果是今天，只显示时间
    if (diffInDays === 0) {
      return time.toLocaleTimeString('zh-TW', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      });
    }
    // 如果是昨天或更早，显示日期和时间
    else if (diffInDays === 1) {
      return '昨天 ' + time.toLocaleTimeString('zh-TW', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      });
    }
    // 更早的消息显示完整日期
    else {
      return time.toLocaleString('zh-TW', {
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    }
  };

  // 检查是否是当前用户的消息
  const isCurrentUserMessage = (message) => {
    if (!message || !user) return false;
    
    const currentUserId = user.id?.toString();
    const messageSenderId = message.senderId?.toString();
    
    // 判断是否是自己的消息
    const isOwnMessage = messageSenderId === currentUserId || 
                        message.senderId === user.id ||
                        message.senderId === user.id?.toString();
    
    // 添加调试日志
    console.log('Message position check:', {
      messageSenderId,
      currentUserId,
      messageContent: message.content,
      isOwnMessage,
      position: isOwnMessage ? 'RIGHT (own)' : 'LEFT (other)'
    });
    
    return isOwnMessage;
  };

  // 计算剩余时间
  const getTimeLeft = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;
    
    if (diff <= 0) return '已過期';
    
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    
    if (hours > 0) {
      return `${hours}小時${minutes}分鐘後過期`;
    } else {
      return `${minutes}分鐘後過期`;
    }
  };

  if (isLoading) {
    return (
      <div className="chatroom-loading">
        <div className="loading-spinner"></div>
        <p>載入聊天室中...</p>
      </div>
    );
  }

  if (error && !chatRoom) {
    return (
      <div className="chatroom-error">
        <h2>無法載入聊天室</h2>
        <p>{error}</p>
        <button onClick={goBack} className="menu-btn">
          返回上一頁
        </button>
      </div>
    );
  }

  return (
    <div className="chatroom-container">
      {/* AI 咨询风格的头部 */}
      <header className="ai-header">
        <button className="menu-btn" onClick={goBack} title="返回上一页">
          <i className="fas fa-arrow-left"></i>
        </button>
        <h2 className="ai-title">
          NoMatch 聊天室 
          {USE_MOCK_API ? ' (演示模式)' : ''}
          {!USE_MOCK_API && (
            <span className={`connection-status ${isConnected ? 'connected' : 'disconnected'}`}>
              {isConnected ? ' ●' : ' ○'}
            </span>
          )}
        </h2>
        <button className="new-chat-btn" onClick={leaveChatRoom} title="離開聊天室">
          <i className="fas fa-times"></i>
        </button>
      </header>

      {/* 聊天室信息介绍 */}
      <div className="chat-intro">
        <div className="partner-info-card">
          <img 
            src={chatRoom?.partnerAvatar} 
            alt={chatRoom?.partnerName}
            className="partner-avatar"
          />
          <div className="partner-details">
            <h3>{chatRoom?.partnerName}</h3>
            <p className="status-info">
              {isTyping ? (
                <span className="typing-status">
                  <i className="fas fa-circle"></i>
                  <i className="fas fa-circle"></i>
                  <i className="fas fa-circle"></i>
                  正在輸入...
                </span>
              ) : (
                <>
                  <span className="online-status online" title="在線">●</span>
                  在線 · {chatRoom && getTimeLeft(chatRoom.expiresAt)}
                </>
              )}
            </p>
          </div>
        </div>
        <p className="chat-description">
          與陌生人進行24小時限時匿名聊天，分享想法，交流體驗，建立短暫而珍貴的連結
        </p>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      {/* 消息列表 */}
      <div className="messages-container">
        <div className="messages-list">
          {messages.map((message) => (
            <div 
              key={message.id} 
              className={`message ${isCurrentUserMessage(message) ? 'own-message' : 'other-message'}`}
            >
              <div className="message-content">
                <p>{message.content}</p>
                <span className="message-time">
                  {formatMessageTime(message.timestamp)}
                </span>
              </div>
            </div>
          ))}
          
          {/* 发送消息时的打字指示器 - 参考 AiConsultation */}
          {isSending && (
            <div className="message own-message sending-message">
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* 消息输入区域 - 参考 AiConsultation 的设计 */}
      <div className="user-input-container">
        <textarea
          ref={textareaRef}
          value={newMessage}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="輸入你的消息..."
          rows={1}
          disabled={isSending}
        />
        <button 
          onClick={sendMessage} 
          className={`send-btn ${isSending ? 'disabled' : ''}`}
          disabled={!newMessage.trim() || isSending}
        >
          {isSending ? (
            <div className="sending-icon">
              <i className="fas fa-spinner fa-spin"></i>
            </div>
          ) : (
            <i className="fas fa-paper-plane"></i>
          )}
        </button>
      </div>
    </div>
  );
};

export default ChatRoom; 